import { useTranslation } from 'react-i18next';

import { formatDateTime, formatNumber } from '../i18n.js';

export interface UptimePoint {
  t: string;
  checks: number;
  failed: number;
  avgResponseMs: number | null;
}

const WIDTH = 600;
const HEIGHT = 160;

/**
 * Response time bars (site-dashboard.html .chart): a bar per check, hour or day; a period with
 * failed checks gets the outage colour behind its bar, plus a summary in words, so colour is not
 * the only signal. The slow threshold shows as a dashed line when the bars reach near it.
 */
export function UptimeChart({
  points,
  uptimePct,
  timeZone,
  unit = 'check',
  slowMs = 3000,
}: {
  points: UptimePoint[];
  uptimePct: number | null;
  timeZone?: string | undefined;
  /** What one bar stands for: the 24 h range shows every check, 7 days hours, longer ones days. */
  unit?: 'check' | 'hour' | 'day';
  slowMs?: number;
}) {
  const { t, i18n } = useTranslation();
  if (points.length === 0) return <p className="empty-chart">{t('site.chart.empty')}</p>;

  const values = points.map((p) => p.avgResponseMs ?? 0);
  const top = Math.max(...values);
  // The threshold line only when the bars get close to it; else it would squash them flat.
  const showSlow = top >= slowMs * 0.6;
  const max = Math.max(500, top, showSlow ? slowMs * 1.15 : 0);
  const step = WIDTH / points.length;
  const y = (ms: number) => HEIGHT - Math.max(2, (ms / max) * (HEIGHT - 8));
  const failedBuckets = points.filter((p) => p.failed > 0).length;
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  const summary = [
    uptimePct === null
      ? null
      : t('site.chart.uptime', { value: formatNumber(uptimePct, i18n.language) }),
    `${t('site.responseTime')} (Median): ${median} ms`,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <figure className="bars">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${summary}. ${t('site.chart.description')}`}
      >
        {[40, 80, 120].map((line) => (
          <line
            key={line}
            x1="0"
            y1={line}
            x2={WIDTH}
            y2={line}
            stroke="var(--line)"
            strokeDasharray="3 4"
          />
        ))}
        {points.map((p, i) =>
          p.failed > 0 ? (
            <rect
              key={`out-${p.t}`}
              data-outage=""
              x={i * step}
              y="0"
              width={step}
              height={HEIGHT}
              fill="var(--bad-soft)"
            />
          ) : null,
        )}
        {points.map((p, i) => (
          <rect
            key={p.t}
            data-bar=""
            x={i * step + step * 0.2}
            y={y(p.avgResponseMs ?? 0)}
            width={Math.max(1, step * 0.6)}
            height={HEIGHT - y(p.avgResponseMs ?? 0)}
            fill={p.failed > 0 ? 'var(--bad)' : 'var(--acc)'}
          >
            <title>
              {t('site.chart.point', {
                time: formatDateTime(p.t, i18n.language, timeZone),
                ms: p.avgResponseMs ?? '–',
                failed: p.failed,
                checks: p.checks,
              })}
            </title>
          </rect>
        ))}
        {showSlow ? (
          <line
            x1="0"
            y1={y(slowMs)}
            x2={WIDTH}
            y2={y(slowMs)}
            stroke="var(--warn)"
            strokeDasharray="4 4"
          />
        ) : null}
      </svg>
      <figcaption className="legend">
        <span>
          <i className="acc" aria-hidden="true" />
          {t(`site.chart.per.${unit}`)}
        </span>
        {showSlow ? (
          <span>
            <i className="warn" aria-hidden="true" />
            {t('site.chart.slow', { ms: formatNumber(slowMs, i18n.language, 0) })}
          </span>
        ) : null}
        {failedBuckets > 0 ? (
          <span>
            <i className="out" aria-hidden="true" />
            {t('site.chart.outage')}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
